import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, matchPath } from "react-router-dom";
import { MetrikaRouteTracker } from "./components/common/MetrikaRouteTracker";

// Маршрут остаётся ленивым, но после preload() рендерится СИНХРОННО.
//
// Одного lazy() мало: React при первом рендере всё равно вызывает loader и
// приостанавливается на тик — даже когда модуль уже лежит в кэше webpack.
// Этого тика хватает, чтобы Suspense показал <PageLoader/> и стёр
// pre-rendered разметку. Поэтому preload() запоминает готовый компонент, и
// обёртка отдаёт его напрямую, минуя Suspense.
//
// При клиентской навигации на другой маршрут _resolved пуст — там работает
// обычный ленивый путь со спиннером, как и задумано.
const route = (loader) => {
  const Lazy = lazy(loader);
  const Route = (props) => {
    const Resolved = Route._resolved;
    return Resolved ? <Resolved {...props} /> : <Lazy {...props} />;
  };
  Route.preload = () =>
    Promise.resolve(loader()).then((m) => {
      Route._resolved = m.default || m;
      return m;
    });
  return Route;
};

// Единая таблица маршрутов: из неё рендерятся <Route> и по ней же
// подбирается чанк для предзагрузки. Две копии списка разъехались бы.
const ROUTES = [
  { path: "/",                             C: route(() => import("./pages/HomePage")) },
  { path: "/uslugi",                       C: route(() => import("./pages/ServicesPage")) },
  { path: "/uslugi/crm",                   C: route(() => import("./pages/CRMPage")) },
  { path: "/uslugi/prodvizhenie",          C: route(() => import("./pages/PromotionPage")) },
  { path: "/uslugi/sayty",                 C: route(() => import("./pages/LandingsPage")) },
  { path: "/uslugi/sayty/lending",         C: route(() => import("./pages/dev/LendingPage")) },
  { path: "/uslugi/sayty/corporate",       C: route(() => import("./pages/dev/CorporatePage")) },
  { path: "/uslugi/sayty/shop",            C: route(() => import("./pages/dev/ShopPage")) },
  { path: "/uslugi/sayty/saas",            C: route(() => import("./pages/dev/SaasPage")) },
  { path: "/uslugi/bi-analitika",          C: route(() => import("./pages/BIAnalyticsPage")) },
  { path: "/uslugi/bi-analitika/dlya-klinik", C: route(() => import("./pages/BiClinicsPage")) },
  { path: "/dop-uslugi",                   C: route(() => import("./pages/AdditionalServicesPage")) },
  { path: "/o-nas",                        C: route(() => import("./pages/AboutPage")) },
  { path: "/blog",                         C: route(() => import("./pages/BlogPage")) },
  { path: "/blog/:slug",                   C: route(() => import("./pages/BlogPostPage")) },
  { path: "/author/:slug",                 C: route(() => import("./pages/AuthorPage")) },
  { path: "/keysy",                        C: route(() => import("./pages/CasesPage")) },
  { path: "/keysy/:slug",                  C: route(() => import("./pages/CasePage")) },
  { path: "/kviz",                         C: route(() => import("./pages/KvizPage")) },
  { path: "/kalkulyator",                  C: route(() => import("./pages/KvizPage")) },
  { path: "/voprosy-otvety",               C: route(() => import("./pages/VoprosyOtvetyPage")) },
  { path: "/faq",                          C: route(() => import("./pages/VoprosyOtvetyPage")) },
  { path: "/kontakty",                     C: route(() => import("./pages/ContactsPage")) },
  { path: "/thank-you",                    C: route(() => import("./pages/ThankYouPage")) },
  { path: "/privacy-policy",               C: route(() => import("./pages/PrivacyPage")) },
  { path: "/consent",                      C: route(() => import("./pages/ConsentPage")) },
  { path: "*",                             C: route(() => import("./pages/NotFoundPage")) },
];

/**
 * Загружает чанк маршрута, соответствующего pathname.
 *
 * Вызывается из index.js ПЕРЕД hydrateRoot. Без этого React при гидратации
 * упирается в неразрешённый lazy, suspend'ится, выбрасывает весь
 * pre-rendered HTML и показывает <PageLoader/>. Замер на проде (Chrome 151,
 * 4x CPU, slow 4G) показывал: контент отрисован на 1.5с, в 3.5с заменён
 * спиннером, вернулся только в 6.1с — и LCP фиксировался именно там.
 */
export const preloadRoute = (pathname) => {
  const hit =
    ROUTES.find((r) => r.path !== "*" && matchPath({ path: r.path, end: true }, pathname)) ||
    ROUTES[ROUTES.length - 1];
  let chunk;
  try {
    // Сбой загрузки чанка не должен блокировать гидратацию: тогда просто
    // отработает обычный ленивый путь через Suspense.
    chunk = hit.C.preload().catch(() => {});
  } catch {
    chunk = Promise.resolve();
  }

  // Для статьи блога чанка страницы мало: её текст лежит ОТДЕЛЬНЫМ чанком
  // (data/articles/<slug>.js, см. data/blogContent.js). Начни мы гидратацию
  // без него — BlogPostPage отрисовал бы пустое тело поверх пре-рендеренного
  // текста, то есть ту же подмену контента, что описана выше, только внутри
  // статьи. Сам загрузчик тоже тянем через import(): иначе реестр из 92
  // маршрутов на статьи уехал бы в главный бандл, который качают все.
  const article = matchPath({ path: "/blog/:slug", end: true }, pathname);
  if (!article) return chunk;

  const body = import("./data/blogContent")
    .then((m) => m.loadArticle(article.params.slug))
    .catch(() => {});

  return Promise.all([chunk, body]);
};

const PageLoader = () => (
  <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
    <div className="w-10 h-10 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <MetrikaRouteTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {ROUTES.map(({ path, C }) => (
              <Route key={path} path={path} element={<C />} />
            ))}
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
